import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";

/**
 * <AffordabilityWidget amount={14640} />
 *
 * Embeds Razorpay's Affordability (EMI²) widget on the page — shows the
 * buyer available EMI plans, cardless-EMI, pay-later and bank offers
 * BEFORE they hit Buy Now. Boosts conversion on higher-ticket items.
 *
 * Spec: https://razorpay.com/docs/payments/payment-gateway/emi²/widget/native-web/
 *
 * Props:
 *   amount   — the product price, in INR (rupees, not paise).
 *              We convert to paise internally as the SDK requires.
 *   className — optional wrapper class.
 *   testId    — data-testid for the wrapper (defaults to `razorpay-emi-widget`).
 *
 * Rendering notes:
 * - The Razorpay SDK targets a div with id `razorpay-affordability-widget`.
 *   Multiple widgets on the same page would collide, so we generate a
 *   unique id per instance and set the SDK's target via `containerId`
 *   (supported by the widget config).
 * - The SDK script is loaded once and cached on `window.__rzp_emi_loaded`.
 * - Guarded on INR-only + amount >= ₹100 (Razorpay EMI floor).
 */
const SCRIPT_URL = "https://cdn.razorpay.com/widgets/affordability/affordability.js";

function loadScript() {
  return new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(false);
    if (window.RazorpayAffordabilitySuite) return resolve(true);
    if (window.__rzp_emi_loading) {
      window.__rzp_emi_loading.then(resolve);
      return;
    }
    window.__rzp_emi_loading = new Promise((res) => {
      const s = document.createElement("script");
      s.src = SCRIPT_URL;
      s.async = true;
      s.onload = () => res(true);
      s.onerror = () => res(false);
      document.body.appendChild(s);
    });
    window.__rzp_emi_loading.then(resolve);
  });
}

export default function AffordabilityWidget({ amount, className = "", testId = "razorpay-emi-widget" }) {
  const containerId = useRef(`razorpay-affordability-widget-${Math.random().toString(36).slice(2, 10)}`);
  const [ready, setReady] = useState(false);
  const [keyId, setKeyId] = useState(null);
  const [failed, setFailed] = useState(false);
  const [hideLoader, setHideLoader] = useState(false);

  // Fetch public key from backend (safer than baking into REACT_APP env).
  useEffect(() => {
    let alive = true;
    api.get("/razorpay/public-key")
      .then((r) => { if (alive) setKeyId(r.data?.key_id || null); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!keyId || !amount || amount <= 0) return;
    let cancelled = false;
    (async () => {
      const ok = await loadScript();
      if (!ok || cancelled) { setFailed(true); return; }
      // Give the SDK a tick to bind after script load.
      setTimeout(() => {
        try {
          const paise = Math.round(Number(amount) * 100);
          const rzpAffordabilitySuite = new window.RazorpayAffordabilitySuite({
            key: keyId,
            amount: paise,
            containerId: containerId.current,
          });
          rzpAffordabilitySuite.render();
          setReady(true);
        } catch (e) {
          setFailed(true);
        }
      }, 60);
      // If the widget hasn't produced any DOM inside our container within
      // 3s, hide the "Loading…" text so we don't sit on stuck copy — this
      // usually means the merchant hasn't yet enabled the widget in the
      // Razorpay dashboard (Affordability → Enable Widget).
      setTimeout(() => setHideLoader(true), 3000);
    })();
    return () => { cancelled = true; };
  }, [keyId, amount]);

  if (failed) return null; // Fail silently — widget is an enhancement, not required.

  return (
    <div className={className} data-testid={testId}>
      {/* SDK renders into this div by id (see containerId). */}
      <div id={containerId.current} />
      {!ready && !hideLoader && (
        <div className="text-[11px] text-muted opacity-60 py-2">
          Loading EMI &amp; pay-later options…
        </div>
      )}
    </div>
  );
}
